import { State, Selector, Action, StateContext } from '@ngxs/store';
import {
    AddItemToWishListAction,
    AddItemToWishListSuccessAction,
    AddItemToWishListFailAction,
    RemoveItemFromWishListAction,
    RemoveItemFromWishListSuccessAction,
    RemoveItemFromWishListFailAction,
    ClearItemsFromWishListAction,
    ClearItemsFromWishListSuccessAction,
    ClearItemsFromWishListFailAction,
    LoadWishListAction,
    LoadWishListSuccessAction,
    LoadWishListFailAction,
    ClearWishListStateAction
} from './wish-list.actions';
import { ClearCartCommand, ClearWishlistCommand, WishlistClient, WishlistModel } from '@core/services/api.service';
import { tap, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { OpenToastrAction } from '@ngxs-store/ui-store/ui.actions';
import { ToastrType } from '@core/models/ui/toastr-config';


export class WishListStateModel {
    public isLoading: boolean;
    public items: WishlistModel;
}

@State<WishListStateModel>({
    name: 'wishList',
    defaults: {
        isLoading: false,
        items: new WishlistModel()
    }
})

@Injectable()
export class WishListState {
    constructor(private wishlistService: WishlistClient, private toaster: ToastrService
    ) { }

    @Selector()
    static isLoading(state: WishListStateModel) {
        return state.isLoading;
    }

    @Selector()
    static getItems(state: WishListStateModel) {
        return state.items;
    }
    @Selector()
    static getNoOfItems(state: WishListStateModel) {
        return state.items.numberOfItems ?? 0;
    }

    // Add To WishList
    @Action(AddItemToWishListAction)
    addItemToWishListAction({ patchState, getState, dispatch }: StateContext<WishListStateModel>, action: AddItemToWishListAction) {
        const state = getState();
        patchState({
            isLoading: true
        });

        return this.wishlistService.addItemToWishlist(action.payload.command).pipe(
            tap((result) => dispatch(new AddItemToWishListSuccessAction({ WishlistModel: result }))),
            catchError((err) => dispatch(new AddItemToWishListFailAction(err))),

        );
    }

    @Action(AddItemToWishListSuccessAction)
    addItemsToCartSuccessAction(ctx: StateContext<WishListStateModel>, action: AddItemToWishListSuccessAction) {
        ctx.patchState({
            isLoading: false,
            items: action.payload.WishlistModel,
        });

        ctx.dispatch(new OpenToastrAction({ message: 'Item added to wishlist', type: ToastrType.success, title: 'Success' }));
    }

    @Action(AddItemToWishListFailAction)
    addItemToWishListFailAction(ctx: StateContext<WishListStateModel>, action: AddItemToWishListFailAction) {
        ctx.patchState({
            isLoading: false,
            items: null,
        });
        this.toaster.success("Couldn't add item, please  try again.", "Wishlist Item", { timeOut: 3000 });
    }


    // remove  item from wishlist
    @Action(RemoveItemFromWishListAction)
    removeItemFromWishListAction({ patchState, getState, dispatch }: StateContext<WishListStateModel>, action: RemoveItemFromWishListAction) {
        const state = getState();
        patchState({
            isLoading: true
        });

        return this.wishlistService.removeItemFromWishlist(action.payload.command).pipe(
            tap((result) => dispatch(new RemoveItemFromWishListSuccessAction({ wishlistItem: result }))),
            catchError((err) => dispatch(new RemoveItemFromWishListFailAction())),
        );
    }

    @Action(RemoveItemFromWishListSuccessAction)
    removeItemFromWishListSuccessAction(ctx: StateContext<WishListStateModel>, action: RemoveItemFromWishListSuccessAction) {
        ctx.patchState({
            isLoading: false,
            items: action.payload.wishlistItem,
        });
    }

    @Action(RemoveItemFromWishListFailAction)
    removeItemFromWishListFailAction(ctx: StateContext<WishListStateModel>, action: RemoveItemFromWishListFailAction) {
        ctx.patchState({
            isLoading: false,
            items: null,
        });
        this.toaster.success("Couldn't remove item, please  try again.", "Wishlist Item", { timeOut: 3000 });
    }

    // clear all items from wishlist
    @Action(ClearItemsFromWishListAction)
    clearItemsFromWishListAction({ patchState, getState, dispatch }: StateContext<WishListStateModel>, action: ClearItemsFromWishListAction) {
        patchState({
            isLoading: true
        });

        return this.wishlistService.clearUserWishlist(new ClearWishlistCommand()).pipe(
            tap((result) => dispatch(new ClearItemsFromWishListSuccessAction({ result }))),
            catchError((err) => dispatch(new ClearItemsFromWishListFailAction(err))),
        );
    }

    @Action(ClearItemsFromWishListSuccessAction)
    clearItemsFromWishListSuccessAction(ctx: StateContext<WishListStateModel>, action: ClearItemsFromWishListSuccessAction) {
        ctx.patchState({
            isLoading: false,
        });
        ctx.dispatch(new LoadWishListAction());
    }

    @Action(ClearItemsFromWishListFailAction)
    clearItemsFromWishListFailAction(ctx: StateContext<WishListStateModel>, action: ClearItemsFromWishListFailAction) {
        ctx.patchState({
            isLoading: false
        });
    }

    // Load WishList
    @Action(LoadWishListAction)
    loadWishListAction({ patchState, getState, dispatch }: StateContext<WishListStateModel>, action: LoadWishListAction) {
        patchState({
            isLoading: true
        });

        return this.wishlistService.getUserWishlist().pipe(
            tap((result) => dispatch(new LoadWishListSuccessAction({ WishlistModel: result }))),
            catchError((err) => dispatch(new LoadWishListFailAction(err))),

        );
    }

    @Action(LoadWishListSuccessAction)
    loadWishListSuccessAction(ctx: StateContext<WishListStateModel>, action: LoadWishListSuccessAction) {
        ctx.patchState({
            isLoading: false,
            items: action.payload.WishlistModel,
        });
    }

    @Action(LoadWishListFailAction)
    loadWishListFailAction(ctx: StateContext<WishListStateModel>, action: LoadWishListFailAction) {
        ctx.patchState({
            isLoading: false,
            items: null,
        });
        this.toaster.success("Couldn't load wishlist items", "Wishlist Items", { timeOut: 3000 });
    }

    // Clear state
    @Action(ClearWishListStateAction)
    clearWishListStateAction(ctx: StateContext<WishListStateModel>, action: ClearWishListStateAction) {
        ctx.patchState({
            isLoading: false,
            items: new WishlistModel()
        });
    }

}
